
import { ConnectionPool, getConnectionPool } from "../../core/connectionPool";
import { checkMigrate } from "../../init/sdkInit";
import { request, response } from "../../proto/bundle";
import { File } from "./File";
import { Folder } from "./Folder";
import { uuid } from "../../utils/uuid";
import Long from "long";
export class InfoOfExternalObjects {
    infoOfExternalSharedObjects: response.IInfoOfExternalSharedInResponse;
    constructor(infoOfExternalSharedObjects: response.IInfoOfExternalSharedInResponse) {
        this.infoOfExternalSharedObjects = infoOfExternalSharedObjects;
    }
    getUuid(): string | null {
        const u = this.infoOfExternalSharedObjects?.uuid;
        if (!u) return null;
        return Array.from(u, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    getName(): string | null {
        return this.infoOfExternalSharedObjects?.name || null;
    }
    isFolder(): boolean {
        return this.infoOfExternalSharedObjects?.typeOfObj === 0;
    }
    //write a static method that randomly(hardcoded) gives out n number of InfoOfExternalObjects for testing
    static getTestInfoOfExternalObjects(n: number): InfoOfExternalObjects[] {
        // console.log(`Generating ${n} test InfoOfExternalObjects`);
        const testObjects: InfoOfExternalObjects[] = [];
        for (let i = 0; i < n; i++) {
            const info: response.IInfoOfExternalSharedInResponse = {
                uuid: new Uint8Array([i, i + 1, i + 2]),
                name: `TestObject_${i}`,
                typeOfObj: i % 2 === 0 ? 0 : 1 // Alternate between folder and file
            };
            testObjects.push(new InfoOfExternalObjects(info));
        }
        return testObjects;
    }

}

export class spaceObjInResponse {
    spaceObjInResponse: response.ISpaceObjInResponse;
    constructor(spaceObjInResponse: response.ISpaceObjInResponse) {
        this.spaceObjInResponse = spaceObjInResponse;
    }

    getUserEmails(): string[] | null {
        return this.spaceObjInResponse?.userEmails || null;
    }

    getInfoOfSharedObjs(): InfoOfExternalObjects[] | null {
        if (!this.spaceObjInResponse?.infoOfSharedObjs) return null;
        return this.spaceObjInResponse.infoOfSharedObjs.map(obj => new InfoOfExternalObjects(obj));
    }

    getName(): string | null {
        return this.spaceObjInResponse?.name || null;
    }

    getOperations(): response.SpaceOpPermissions[] | null {
        return this.spaceObjInResponse?.operations || null;
    }
    static getReadOpIds(): number[] {
        return [0, 1, 2, 11, 12, 13, 15]
    }
    static getWriteOpIds(): number[] {
        return [0, 4, 2, 5, 6, 7, 15, 14, 12];
    }
    getOperationsStrings(): string[] | null {
        const opIds = this.getOperations();
        if (!opIds) return null;

        const opIdsNumbers = opIds.map(op => op as number);
        const opStrings: string[] = [];

        // Check if ALL read operation IDs are present in opIdsNumbers
        const hasRead = spaceObjInResponse.getReadOpIds()
            .every(readOpId => opIdsNumbers.includes(readOpId));

        // Check if ALL write operation IDs are present in opIdsNumbers
        const hasWrite = spaceObjInResponse.getWriteOpIds()
            .every(writeOpId => opIdsNumbers.includes(writeOpId));

        if (hasRead) opStrings.push("read");
        if (hasWrite) opStrings.push("write");

        return opStrings;
    }

    getCreationTime(): number | null {
        return this.spaceObjInResponse?.creationTime?.toString()
            ? Number(this.spaceObjInResponse.creationTime)
            : null;
    }

    getUpdatedTime(): number | null {
        return this.spaceObjInResponse?.updatedTime?.toString()
            ? Number(this.spaceObjInResponse.updatedTime)
            : null;
    }

    getMaxSpaceSize(): Long | number | null {
        return this.spaceObjInResponse?.maxSpaceSize
            ? Number(this.spaceObjInResponse.maxSpaceSize)
            : null;
    }

    getMaxFileSize(): number | null {
        return this.spaceObjInResponse?.maxFileSize
            ? Number(this.spaceObjInResponse.maxFileSize)
            : null;
    }

    getObjects(): any[] | null {
        return this.spaceObjInResponse?.objects || null;
    }

    getAllowedFileTypes(): string[] | null {
        return this.spaceObjInResponse?.allowedFileTypes || null;
    }

    getSizeConsumed(): number | null {
        return this.spaceObjInResponse?.sizeConsumed
            ? Number(this.spaceObjInResponse.sizeConsumed)
            : null;
    }

    getDeniedFileTypes(): string[] | null {
        return this.spaceObjInResponse?.deniedFileTypes || null;
    }

    getAllowedLocations(): string[] | null {
        return this.spaceObjInResponse?.allowedLocations || null;
    }

    getDeniedLocations(): string[] | null {
        return this.spaceObjInResponse?.deniedLocations || null;
    }

    getAllowedRegions(): string[] | null {
        return this.spaceObjInResponse?.allowedRegions || null;
    }

    getDeniedRegions(): string[] | null {
        return this.spaceObjInResponse?.deniedRegions || null;
    }

    getAllowedIps(): string[] | null {
        return this.spaceObjInResponse?.allowedIps || null;
    }

    getDeniedIps(): string[] | null {
        return this.spaceObjInResponse?.deniedIps || null;
    }

    getUserTillNow(): number | null {
        return this.spaceObjInResponse?.userTillNow || null;
    }

    getObjectsTillNow(): number | null {
        return this.spaceObjInResponse?.objectsTillNow || null;
    }

}

export class ExternalShare {
    owner: String;
    constructor(owner: String) {
        this.owner = owner;
    }
    static async listSpacesOfUser(): Promise<spaceObjInResponse[]> {
        try {
            // console.log("about to get connection pool");
            const pool = await getConnectionPool();

            // console.log("about to send req");
            const req = request.Request.create({
                id: null,
                reqCreateTime: Date.now(),
                listSpaces: request.ListSpaces.create({
                    pageSize: null,
                    pageToken: null
                })
            });

            const resp = await pool.sendAndWait(req);
            // console.log("received resp");

            checkMigrate(resp.listSpaces.status);

            if (!resp.listSpaces || resp.listSpaces.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to get spaces of user (${resp.listSpaces?.status})`);
            }
            const spaceObjects = resp.listSpaces.spaceObjects || [];
            return spaceObjects.map(obj => new spaceObjInResponse(obj));

        } catch (err) {
            console.error("❌ Error loading user spaces:", err);
            // Optional: rethrow if caller should know
            throw err;
        }
    }

    //    static async listSpacesOfUser(): Promise<response.ISpaceObjInResponse[]> {
    //     console.log("abotu to get connection pool");
    //         const pool = await getConnectionPool();
    //             console.log("about to send req,",pool);
    //         let req = request.Request.create({
    //             id: null,
    //             reqCreateTime: Date.now(),
    //             listSpaces: request.ListSpaces.create({
    //                 pageSize: null,
    //                 pageToken: null
    //             })
    //         });
    //         const resp = await pool.sendAndWait(req);
    //          console.log("received resp");

    //         checkMigrate(resp.listSpaces.status)
    //         if (!resp.listSpaces || resp.listSpaces.status !== response.Status.SUCCESS) {
    //             throw new Error(`Failed to get spaces of user (${resp.listSpaces?.status})`);
    //         }
    //         const result = resp.listSpaces;
    //         const space_objects = result.spaceObjects || [];
    //         return space_objects;
    //     }
    static async addUserToSpace(email: string, spaceName: string): Promise<void> {
        try {

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    addUserToSpace: request.AddUserToSpace.create({
                        email,
                        spaceName


                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.addUserToSpace.status);

            if (!resp.addUserToSpace || resp.addUserToSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to remove space   (${resp.addUserToSpace?.status})`);
            }
        } catch (err) {
            console.error("❌ Error adding user to space:", err);
            // Optional: rethrow if caller should know
            throw err;
        }
    }

    static async addObjectToSpace(typeOfObj: string, typeOfPath: string, fullPath: string, spaceName: string): Promise<void> {
        try {
            // Convert and validate typeOfObj
            const lowerTypeOfObj = typeOfObj.toLowerCase();
            let validatedTypeOfObj: number;
            if (lowerTypeOfObj === "file") {
                validatedTypeOfObj = 1; // TypeOfObj.TypeFile
            } else if (lowerTypeOfObj === "folder") {
                validatedTypeOfObj = 0; // TypeOfObj.TypeFolder
            } else {
                throw new Error('typeOfObj must be "file" or "folder"');
            }

            // Convert and validate typeOfPath
            const lowerTypePath = typeOfPath.toLowerCase();
            let validatedTypePath: number;
            if (lowerTypePath === "normal") {
                validatedTypePath = 1; // TypeOfPath.TFolder
                //then prepend files/ to fullpath
                // fullPath = "files/" + fullPath.replace(/^\/+/, '');
            } else if (lowerTypePath === "shared_team") {
                validatedTypePath = 2; // TypeOfPath.TShareTeam
            } else if (lowerTypePath === "shared_self") {
                validatedTypePath = 0; // TypeOfPath.TShareUser
            } else {
                throw new Error('typeOfPath must be "normal", "shared_team", or "shared_self"');
            }
            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    addObjectToSpace: request.AddObjectToSpace.create({
                        typeOfObj: validatedTypeOfObj,
                        typeOfPath: validatedTypePath,
                        pathOfObj: fullPath,
                        spaceName
                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.addObjectToSpace.status);

            if (!resp.addObjectToSpace || resp.addObjectToSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to add object to space   (${resp.addObjectToSpace?.status})`);
            }
        } catch (err) {
            console.error("❌ Error adding object to space:", err);
            // Optional: rethrow if caller should know
            throw err;
        }
    }
    static async updateSpaceOperations(operations: string[], name: string, allowedIps: string[], deniedIps: string[], allowedCountries: string[], deniedCountries: string[], allowedRegions: string[], deniedRegions: string[], alloedFileTypes: string[], deniedFileTypes: string[], maxSpaceSize?: number, maxFileSize?: number): Promise<void> {
        try {
            //operations either will have "read" or "write" or both
            const opIds: request.SpaceOpPermissions[] = [];
            if (operations.includes("read")) {
                opIds.push(...spaceObjInResponse.getReadOpIds().map(id => id as request.SpaceOpPermissions));
            }
            if (operations.includes("write")) {
                opIds.push(...spaceObjInResponse.getWriteOpIds().map(id => id as request.SpaceOpPermissions));
            }

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    updatePermissionsOfSpace: request.UpdatePermissionsOfSpace.create({
                        operations: opIds,
                        spaceName: name,
                        allowedCountries: allowedCountries,
                        deniedCountries: deniedCountries,
                        allowedIps: allowedIps,
                        deniedIps: deniedIps,
                        allowedFileTypes: alloedFileTypes,
                        deniedFileTypes: deniedFileTypes,
                        allowedRegions: allowedRegions,
                        deniedRegions: deniedRegions,
                        maxFileSize: maxFileSize,
                        maximumSpaceSize: maxSpaceSize
                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.updatePermissionsOfSpace.status);

            if (!resp.updatePermissionsOfSpace || resp.updatePermissionsOfSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to update space operations   (${resp.updatePermissionsOfSpace?.status})`);
            }

        } catch (err) {
            console.error("❌ Error updating space operations:", err);
            // Optional: rethrow if caller should know
            throw err;
        }


    }

    static async SignInToSpace(otp: string, externalUserEmail: string, token: string, ownerEmail: string, spaceName: string): Promise<InfoOfExternalObjects[]> {
        try {

            //convert token:string to bytes by doing hexdecode
            const signInToSpaceReq = request.SignInToSpace.create({
                ownerEmail,
                token,
                externalUserEmail,
                spaceName,
                otp


            });
            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    signInToSpace: signInToSpaceReq
                }
            );
            const pool = await getConnectionPool();

            const resp = await pool.sendAndWait(req);
            pool.setEmail(externalUserEmail);
            checkMigrate(resp.signInToSpace.status);

            if (!resp.signInToSpace || resp.signInToSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed toSIGNIN  (${resp.signInToSpace?.status})`);
            }
            // if its success,then set the signinforexternal user in pool
            pool.setSignInRequestForExternalUser(signInToSpaceReq);
            const infoSharedObjects = resp.signInToSpace.infoSharedObjects || [];
            return infoSharedObjects.map(obj => new InfoOfExternalObjects(obj));
        } catch (err) {
            console.error("❌ Error SIGNING IN :", err);
            // Optional: rethrow if caller should know
            throw err;
        }
    }
    static async SendOtpIfNotExist(ownerEmail: string, token: string, externalUserEmail: string, spaceName: string): Promise<void> {
        try {

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    sendOtpToExternalUserIfNotExist: request.SendOtpToExternalUserIfNotExist.create({
                        ownerEmail,
                        token,
                        externalUserEmail,
                        spaceName


                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.sendOtpToExternalUserIfNotExist.status);

            if (!resp.sendOtpToExternalUserIfNotExist || resp.sendOtpToExternalUserIfNotExist.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to send otp   (${resp.sendOtpToExternalUserIfNotExist?.status})`);
            }
        } catch (err) {
            console.error("❌ Error sending otp:", err);
            // Optional: rethrow if caller should know
            throw err;
        }

    }
    static async SendOtp(ownerEmail: string, token: string, externalUserEmail: string, spaceName: string): Promise<void> {
        try {

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    sendOtpToExternalUser: request.SendOtpToExternalUser.create({
                        ownerEmail,
                        token,
                        externalUserEmail,
                        spaceName


                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.sendOtpToExternalUser.status);

            if (!resp.sendOtpToExternalUser || resp.sendOtpToExternalUser.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to send otp   (${resp.sendOtpToExternalUser?.status})`);
            }
        } catch (err) {
            console.error("❌ Error sending otp:", err);
            // Optional: rethrow if caller should know
            throw err;
        }

    }
    static async removeObjectFromSpace(uuid_hexa: string, spaceName: string): Promise<void> {
        try {
            //convert uuid_hexa to bytes
            const req = request.Request.create({
                id: null,
                reqCreateTime: Date.now(),
                removeObjectFromSpace: request.RemoveObjectFromSpace.create({
                    uuid: uuid_hexa,
                    spaceName
                })
            });
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.removeObjectFromSpace.status);

            if (!resp.removeObjectFromSpace || resp.removeObjectFromSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to remove object from space   (${resp.removeObjectFromSpace?.status})`);
            };
        } catch (err) {
            console.error("❌ Error removing object from space:", err);
            // Optional: rethrow if caller should know
            throw err;
        }
    }
    static async removeUserFromSpace(email: string, spaceName: string): Promise<void> {
        try {

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    removeUserFromSpace: request.RemoveUserFromSpace.create({
                        email,
                        spaceName


                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.removeUserFromSpace.status);

            if (!resp.removeUserFromSpace || resp.removeUserFromSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to remove space   (${resp.removeUserFromSpace?.status})`);
            }
        } catch (err) {
            console.error("❌ Error removing user from space:", err);
            // Optional: rethrow if caller should know
            throw err;
        }
    }
    static async addSpace(operations: [], name: string): Promise<spaceObjInResponse> {
        try {

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    addSpace: request.AddSpace.create({
                        operations: operations,
                        name: name,
                    })
                }
            );


            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.addSpace.status);

            if (!resp.addSpace || resp.addSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to add space  user (${resp.addSpace?.status})`);
            }
            return new spaceObjInResponse(resp.addSpace.spaceObject);


        } catch (err) {
            console.error("❌ Error loading user spaces:", err);
            // Optional: rethrow if caller should know
            throw err;
        }

    }
    static async RemoveSpace(name: string): Promise<void> {
        try {

            const req = request.Request.create(
                {
                    id: null,
                    reqCreateTime: Date.now(),
                    removeSpace: request.RemoveSpace.create({
                        spaceName: name

                    })
                }
            );
            const pool = await getConnectionPool();
            const resp = await pool.sendAndWait(req);

            checkMigrate(resp.removeSpace.status);

            if (!resp.removeSpace || resp.removeSpace.status !== response.Status.SUCCESS) {
                throw new Error(`Failed to remove space   (${resp.removeSpace?.status})`);
            }
        } catch (err) {
            console.error("❌ Error removing space:", err);
            // Optional: rethrow if caller should know
            throw err;
        }

    }
    static search_sizes(sizes: (number | Long)[] | null | undefined,
        versions: Uint8Array<ArrayBufferLike>[] | null | undefined,
        currentVersion: Uint8Array<ArrayBufferLike> | null | undefined): number {
        if (!sizes || !versions || !currentVersion) {
            throw new Error("Sizes, versions or current version is null or undefined");
        }
        for (let i = 0; i < sizes.length; i++) {
            if (versions[i] && versions[i].byteLength > 0) {
                if (versions[i].byteLength === currentVersion.byteLength &&
                    versions[i].every((byte, index) => byte === currentVersion[index])) {
                    let size = sizes[i];
                    if (typeof size === "number") {
                        return size;
                    } else {
                        return size.toNumber();
                    }
                }
            }
        }
        throw new Error("Can't find current version in versions");
    }

    static async getFolderList(path: string, order?: number): Promise<(File | Folder)[]> {
        //  TODO:implement caching mechanism here later or modify the logic in Folder.ts to support all kinds of listing
        //this.OrderBy if undefined, default to 0 (by name)
        const orderBy = order ?? 0;
        const pool = await getConnectionPool();
        let page_token = null;
        const list = [];
        // let lastModifiedDate = this.getLastModifiedDate();
        // let should_add_to_cache = false;
        // const path = this.in_trash ? "/trash/" : ("/files/" + this.parentPath + "/" + this.folderName);
        // if (this.folderName && this.folderName.trim() !== "" && !/^[\/\s]+$/.test(this.folderName) && !this.in_trash) {
        //     if (globalCache.has(path)) {
        //         const pathl = this.parentPath + "/" + this.folderName;
        //         let item = await Item.getItem(pathl, false);
        //         if (item instanceof Folder) {
        //             const data = globalCache.get(path);
        //             if (item.lastModifiedDate === data.lastModifiedDate) {
        //                 return data.list;
        //             } else {
        //                 lastModifiedDate = item.lastModifiedDate;
        //             }
        //         }
        //     } else {
        //         const pathl = this.parentPath + "/" + this.folderName;
        //         let item = await Item.getItem(pathl, false);
        //         if (item instanceof Folder) {
        //             lastModifiedDate = item.lastModifiedDate;
        //         }
        //     }
        //     should_add_to_cache = true;
        // }
        while (true) {
            const req = request.Request.create({
                id: null,
                reqCreateTime: Date.now(),
                list: request.List.create({
                    path: path,
                    type: request.ListType.FilesAndFolders,
                    pageSize: 100,
                    orderBy: orderBy,
                    pageToken: page_token,
                    typeOfPath: 0
                })
            });
            const resp = await pool.sendAndWait(req);
            checkMigrate(resp.list.status)
            if (resp.list?.status !== 0) {
                throw new Error(`Failed to list folder at path ${path} (${resp.list?.status})`);
            }
            for (const item of resp.list.objects ?? []) {
                if (item.fileMeta) {
                    const file = new File();
                    file.setFileUuid(item.fileMeta.uuid ?? new Uint8Array(0));
                    // file.setParentPath(this.parentPath + "/" + this.folderName);
                    file.setParentPath(path);
                    file.setFileName(item.fileMeta.name ?? "");
                    file.setNew(false);

                    file.setSize(ExternalShare.search_sizes(item.fileMeta.sizes, item.fileMeta.versions, item.fileMeta.currentVersion));
                    file.setSizes(item.fileMeta.sizes ?? []);
                    file.setVersions(item.fileMeta.versions ?? []);
                    file.setVersionUuid(item.fileMeta.currentVersion ?? new Uint8Array(0));
                    file.setCurrentVersion(item.fileMeta.currentVersion ?? new Uint8Array(0));

                    file.setCreationDate(item.fileMeta.creationDate ?? 0);
                    file.setLastModifiedDate(item.fileMeta.lastModifiedDate ?? 0);
                    file.setOwner(item.fileMeta.owner ?? "");
                    list.push(file);
                }
                else if (item.folderMeta) {
                    const folder = new Folder();
                    folder.setUuId(item.folderMeta.uuid ?? new Uint8Array(0));
                    folder.setFolderName(item.folderMeta.name ?? "");
                    // folder.setParentPath(this.parentPath + "/" + this.folderName);
                    folder.setParentPath(path);
                    folder.setCreationDate(item.folderMeta.creationDate ?? 0);
                    folder.setCurrentPage(-1);
                    folder.setLastModifiedDate(item.folderMeta.lastModifiedDate ?? 0);
                    folder.setOwner(item.folderMeta.owner ?? "");
                    folder.setOrderBy(orderBy);
                    list.push(folder);
                }
                else {
                    throw new Error(`Unknown item type in folder list at path ${path}`);
                }
            }
            page_token = resp.list.pageToken ?? null;
            if (page_token === null || page_token.length === 0) {
                break;
            }
        }

        // if (should_add_to_cache) {
        //     globalCache.set(path, {
        //         list: list,
        //         lastModifiedDate: lastModifiedDate
        //     });
        // }
        return list;
    }


};

